import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Refresh as ResendIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  HourglassEmpty as PendingIcon,
  Warning as ExpiredIcon
} from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
 

const StatusChip = ({ status, expiresAt }) => {
  const { t } = useTranslation();
  let isExpired = false;
  try {
    isExpired = expiresAt ? new Date(expiresAt) < new Date() : true;
  } catch (e) {
    console.error('Invalid date format:', expiresAt);
    isExpired = true;
  }
  
  if (status === 'accepted') {
    return (
      <Chip 
        icon={<CheckIcon fontSize="small" />} 
        label={t('Team_Members.Accepted')} 
        color="success" 
        size="small" 
        variant="outlined"
        sx={{ 
          fontWeight: 'medium',
          minWidth: '90px'
        }}
      />
    );
  } else if (status === 'pending' && !isExpired) {
    return (
      <Chip 
        icon={<PendingIcon fontSize="small" />} 
        label={t('Team_Members.Pending')}
        color="primary" 
        size="small" 
        variant="outlined"
        sx={{ 
          fontWeight: 'medium',
          minWidth: '90px'
        }}
      />
    );
  } else {
    return (
      <Chip 
        icon={<ExpiredIcon fontSize="small" />} 
        label={t('Team_Members.Expired')} 
        color="error" 
        size="small" 
        variant="outlined"
        sx={{ 
          fontWeight: 'medium',
          backgroundColor: 'rgba(211, 47, 47, 0.1)',
          minWidth: '90px'
        }}
      />
    );
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    console.error('Error formatting date:', e);
    return 'Invalid date';
  }
};

const TeamMembersTable = ({ 
  invitations, 
  loading, 
  onResend, 
  onCancel, 
  actionLoading 
}) => {
  const { t } = useTranslation();

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', mb: 4 }}>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader aria-label="team members table">
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '250px' }}>{t('Team_Members.Email')}</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '120px' }}>{t('Team_Members.Role')}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '120px' }}>{t('Team_Members.Status')}</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '150px' }}>{t('Team_Members.Expires')}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '120px' }}>{t('Team_Members.Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : invitations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                 {t('Team_Members.No_Invitations_Found')}
                </TableCell>
              </TableRow>
            ) : (
              invitations.map((invitation) => (
                <TableRow 
                  key={invitation.id || invitation._id}
                  hover
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'medium', py: 0.5 }}>
                    {invitation.email}
                  </TableCell>
                  <TableCell align="left" sx={{ py: 0.5 }}>
                    <Chip 
                      label={invitation.role || 'user'} 
                      size="small"
                      color={invitation.role === 'admin' ? 'primary' : 'default'}
                      variant="outlined"
                      sx={{ 
                        fontWeight: 'medium',
                        textTransform: 'capitalize'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.5 }}>
                    <StatusChip status={invitation.status} expiresAt={invitation.expiresAt} />
                  </TableCell>
                  <TableCell align="left" sx={{ py: 0.5 }}>{formatDate(invitation.expiresAt)}</TableCell>
                  <TableCell align="center" sx={{ py: 0.5 }}>
                    {invitation.status === 'pending' && (
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton 
                          size="small" 
                          color="primary" 
                          sx={{ mx: 0.25, p: 0.5 }}
                          onClick={() => {
                            console.log('Resend clicked for:', invitation.id || invitation._id);
                            onResend(invitation.id || invitation._id);
                          }}
                          disabled={actionLoading}
                        >
                          <ResendIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error" 
                          sx={{ mx: 0.25, p: 0.5 }}
                          onClick={() => {
                            console.log('Cancel clicked for:', invitation.id || invitation._id);
                            onCancel(invitation.id || invitation._id);
                          }}
                          disabled={actionLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TeamMembersTable; 